import { Box, Typography, useTheme, alpha } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import PaidIcon from '@mui/icons-material/Paid';
import BadgeIcon from '@mui/icons-material/Badge';

const formatCurrency = (value?: number) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    const data = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
    const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
    return `${data} · ${hora}`;
  } catch {
    return dateString;
  }
};

interface IDocumentoResumoBarProps {
  numerodocumento?: number;
  emissaodata?: string;
  parceironome?: string;
  valortotal?: number;
  // NFC-e não tem cliente cadastrado na venda (é sempre "Consumidor") — em
  // vez do bloco "Cliente" (que ficaria sempre "—"), a Dialogsendnfce passa
  // esse slot pra mostrar o CPF/nome informado na nota (opcional) como um
  // atalho clicável que abre a dialog de CPF na nota.
  cpfNaNotaSlot?: {
    cpf?: string;
    nome?: string;
    onClick: () => void;
  };
}

// Faixa horizontal única com o essencial da venda sendo faturada (número,
// data/hora, cliente, total) — reaproveitada pelas dialogs de NFC-e e NFe,
// no lugar de vários cards separados (ver Dialogsendnfce.tsx/Dialogsendnfe.tsx).
export const DocumentoResumoBar: React.FC<IDocumentoResumoBarProps> = ({
  numerodocumento, emissaodata, parceironome, valortotal, cpfNaNotaSlot,
}) => {
  const theme = useTheme();
  const corDivisor = theme.palette.mode === 'dark' ? theme.palette.divider : '#DCE5EA';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.25,
        borderBottom: `1px solid ${corDivisor}`,
        bgcolor: alpha(theme.palette.text.primary, 0.02),
        overflowX: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <ShoppingCartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.2 }}>
            Venda #{numerodocumento ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {formatDateTime(emissaodata)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: corDivisor, flexShrink: 0 }} />

      {cpfNaNotaSlot ? (
        <Box
          onClick={cpfNaNotaSlot.onClick}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1,
            cursor: 'pointer', borderRadius: '8px', px: 0.75, py: 0.25, mx: -0.75,
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) },
          }}
        >
          <BadgeIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.2 }}>
              CPF na nota
            </Typography>
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: cpfNaNotaSlot.cpf ? 'text.primary' : 'text.secondary',
              }}
            >
              {cpfNaNotaSlot.cpf
                ? `${cpfNaNotaSlot.cpf}${cpfNaNotaSlot.nome ? ` · ${cpfNaNotaSlot.nome}` : ''}`
                : 'Toque para informar'}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.2 }}>
              Cliente
            </Typography>
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {parceironome || '—'}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: corDivisor, flexShrink: 0 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <PaidIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', lineHeight: 1.2 }}>
            Total
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#00A86B', lineHeight: 1.2 }}>
            {formatCurrency(valortotal)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
